import { useState, type FormEvent, type JSX } from 'react';

import { shaktiApi, ApiError } from '../api/client';
import { useAuthStore } from '../stores/auth';
import { useProjectStore } from '../stores/project';

/**
 * H3.4 (round-4) — in-dashboard project creation.
 *
 * Round-3 shipped a read-only `ProjectSwitcher` and no way to create
 * a project from the UI. A PM/PO who signed in with zero projects
 * saw "No projects" forever and had to `curl POST /v1/projects`.
 *
 * This modal closes the gap. Renders on the `/dashboard/projects`
 * index (H3.4 follow-up) and is opened from the "+" button in the
 * nav sidebar next to the ProjectSwitcher.
 */
export function NewProjectModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (p: { id: string; name: string; slug: string }) => void;
}): JSX.Element {
  const organizationId = useAuthStore((s) => s.organizationId);
  const setActiveProject = useProjectStore((s) => s.setActiveProject);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!organizationId) {
      setError('Not signed in — cannot create a project.');
      return;
    }
    setBusy(true);
    try {
      const row = await shaktiApi.createProject({
        name: name.trim(),
        slug: slug.trim() || autoSlug(name),
      });
      setActiveProject(row.id);
      onCreated({ id: row.id, name: row.name, slug: row.slug });
    } catch (e) {
      setError(
        e instanceof ApiError
          ? `${e.body?.error ?? e.message} (${e.status})`
          : e instanceof Error
            ? e.message
            : 'Create failed',
      );
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-project-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={submit}
        className="shakti-card w-full max-w-md bg-bg-base p-6"
        data-testid="new-project-modal"
      >
        <header className="mb-4 flex items-start justify-between">
          <h3 id="new-project-title" className="text-lg font-semibold">
            New project
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-fg-muted hover:text-fg-primary"
          >
            ×
          </button>
        </header>

        <label className="flex flex-col gap-1 text-xs">
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              // Auto-slugify as the user types unless they've customised the slug.
              if (!slug || slug === autoSlug(name)) {
                setSlug(autoSlug(e.target.value));
              }
            }}
            placeholder="Aurora SaaS"
            required
            disabled={busy}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            data-testid="new-project-name"
          />
        </label>

        <label className="mt-3 flex flex-col gap-1 text-xs">
          Slug
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="aurora-saas"
            pattern="[a-z0-9][a-z0-9\-]*"
            title="Lowercase letters, digits, and hyphens"
            disabled={busy}
            className="shakti-mono rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            data-testid="new-project-slug"
          />
        </label>

        {error ? (
          <div
            role="alert"
            className="mt-3 rounded-sm border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
          >
            {error}
          </div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || name.trim().length < 2}
            className="rounded-sm bg-fg-primary px-3 py-1.5 text-xs font-semibold text-bg-base disabled:opacity-50"
            data-testid="new-project-submit"
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

function autoSlug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
